import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import '../styles/ProfilePage.css';

export default function ProfilePage() {
    const { user, userProfile } = useAuth();

    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const [showPasswordSection, setShowPasswordSection] = useState(false);

    useEffect(() => {
        if (user) {
            setDisplayName(user.displayName || '');
            setEmail(user.email || '');
        }
    }, [user]);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            // Atualizar nome de exibição
            if (displayName !== user.displayName) {
                await updateProfile(user, { displayName });
            }

            // Atualizar email se foi alterado
            if (email !== user.email) {
                await updateEmail(user, email);
            }

            // Atualizar documento do usuário no Firestore
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
                displayName,
                email,
                updatedAt: new Date().toISOString()
            });

            setSuccess('Perfil atualizado com sucesso!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Erro ao atualizar perfil:', err);
            if (err.code === 'auth/requires-recent-login') {
                setError('Por segurança, faça login novamente para alterar o email.');
            } else {
                setError('Erro ao atualizar perfil. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        if (newPassword.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres.');
            return;
        }

        if (!currentPassword) {
            setError('Digite sua senha atual para confirmar a alteração.');
            return;
        }

        setLoading(true);

        try {
            // Reautenticar usuário
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Atualizar senha
            await updatePassword(user, newPassword);

            setSuccess('Senha atualizada com sucesso!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordSection(false);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Erro ao atualizar senha:', err);
            if (err.code === 'auth/wrong-password') {
                setError('Senha atual incorreta.');
            } else {
                setError('Erro ao atualizar senha. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    const currentPlan = userProfile?.plan || 'basico';
    const createdAt = user?.metadata?.creationTime
        ? new Date(user.metadata.creationTime).toLocaleDateString('pt-BR')
        : 'Não disponível';

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-1">Meu Perfil</h1>
                    <p className="text-muted mb-0">Gerencie suas informações pessoais</p>
                </div>
            </div>

            <div className="row">
                {/* Informações da Conta */}
                <div className="col-lg-8">
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h2 className="h5 mb-3">Informações Pessoais</h2>

                            {success && <div className="alert alert-success py-2 small">{success}</div>}
                            {error && <div className="alert alert-danger py-2 small">{error}</div>}

                            <form onSubmit={handleUpdateProfile}>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Nome de exibição</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="Seu nome"
                                    />
                                    <div className="form-text small">
                                        Este nome será exibido no sistema
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Email</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                    <div className="form-text small">
                                        Usado para login e notificações
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? 'Salvando...' : 'Salvar Alterações'}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Seção de Senha */}
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h2 className="h5 mb-0">Segurança</h2>
                                {!showPasswordSection && (
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setShowPasswordSection(true)}
                                    >
                                        Alterar Senha
                                    </button>
                                )}
                            </div>

                            {showPasswordSection && (
                                <form onSubmit={handleUpdatePassword}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Senha atual</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Nova senha</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            minLength="6"
                                            required
                                        />
                                        <div className="form-text small">
                                            Mínimo de 6 caracteres
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Confirmar nova senha</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            minLength="6"
                                            required
                                        />
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? 'Atualizando...' : 'Atualizar Senha'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setShowPasswordSection(false);
                                                setCurrentPassword('');
                                                setNewPassword('');
                                                setConfirmPassword('');
                                                setError('');
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            )}

                            {!showPasswordSection && (
                                <p className="text-muted small mb-0">
                                    Última alteração de senha: Não disponível
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar com informações da conta */}
                <div className="col-lg-4">
                    <div className="card shadow-sm mb-4">
                        <div className="card-body text-center">
                            <div
                                className="mx-auto mb-3"
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '2rem',
                                    color: 'white',
                                    fontWeight: 'bold'
                                }}
                            >
                                {(displayName || email)?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <h5>{displayName || 'Usuário'}</h5>
                            <p className="text-muted small mb-0">{email}</p>
                        </div>
                    </div>

                    <div className="card shadow-sm">
                        <div className="card-body">
                            <h6 className="fw-semibold mb-3">Informações da Conta</h6>

                            <div className="mb-3">
                                <div className="small text-muted">Plano Atual</div>
                                <div className="fw-semibold">
                                    {currentPlan === 'basico' ? 'Básico (Gratuito)' :
                                     currentPlan === 'profissional' ? 'Profissional' :
                                     'Premium'}
                                </div>
                            </div>

                            <div className="mb-3">
                                <div className="small text-muted">Membro desde</div>
                                <div className="fw-semibold">{createdAt}</div>
                            </div>

                            <div className="mb-3">
                                <div className="small text-muted">ID do Usuário</div>
                                <div className="fw-semibold small text-truncate" title={user?.uid}>
                                    {user?.uid}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
